import { useEffect, useState } from 'react';
import { api, errorText } from '../../api';
import { useToast } from '../../lib/toast';
import { darf } from '../tarifHinweis';
import { JahrWahl, type SeitenProps } from './Steuer';
import { basis, euro, herunterladen, type Euer as EuerDaten, type EuerZeile } from './daten';

// Einnahmen-Überschuss-Rechnung (/rechnung/steuer/euer): Gewinn eines Jahres nach Geldeingang und -ausgang, zum
// Übertragen in die Anlage EÜR in ELSTER oder für das Steuerbüro (Excel).
export function Euer({ data }: SeitenProps) {
  const toast = useToast();
  const [jahr, setJahr] = useState(() => Number(data.today.slice(0, 4)));
  const [r, setR] = useState<EuerDaten | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let weg = false;
    setR(null);
    api
      .get<EuerDaten>(`${basis(data.firm.id)}/euer?jahr=${jahr}`)
      .then((x) => !weg && (setR(x), setError(null)))
      .catch((err) => !weg && setError(errorText(err)));
    return () => {
      weg = true;
    };
  }, [data.firm.id, jahr]);

  return (
    <>
      <div className="rt-head">
        <div>
          <h1>Gewinn {jahr}</h1>
          <p className="muted">Einnahmen-Überschuss-Rechnung: zählt, wann das Geld geflossen ist.</p>
        </div>
        <div className="rt-head-act">
          <JahrWahl jahr={jahr} heute={data.today} onChange={setJahr} />
          {r && darf(data.tarif, 'export') && (
            <button
              type="button"
              className="btn"
              onClick={() => void herunterladen(`${basis(data.firm.id)}/euer/export?jahr=${jahr}`, `EÜR ${jahr}.csv`).catch((e) => toast(errorText(e)))}
            >
              Excel
            </button>
          )}
        </div>
      </div>
      {error && <div className="notice bad">{error}</div>}
      {!r ? (
        !error && <div className="spinner" aria-label="Lädt" />
      ) : (
        <>
          <div className="ag-kacheln">
            <div className="ag-kachel">
              <span>Einnahmen</span>
              <b className="num">{euro(r.summeEinnahmen)}</b>
            </div>
            <div className="ag-kachel">
              <span>Ausgaben</span>
              <b className="num">{euro(r.summeAusgaben)}</b>
            </div>
            <div className={`ag-kachel ${r.gewinn >= 0 ? 'ag-gut' : 'ag-schlecht'}`}>
              <span>{r.gewinn >= 0 ? 'Gewinn' : 'Verlust'}</span>
              <b className="num">{euro(Math.abs(r.gewinn))}</b>
            </div>
          </div>
          <Block titel="Einnahmen" zeilen={r.einnahmen} summe={r.summeEinnahmen} />
          <Block titel="Ausgaben" zeilen={r.ausgaben} summe={r.summeAusgaben} />
          {r.anlagen.length > 0 && (
            <div className="rt-card">
              <h2 className="ag-h2">Abschreibungen</h2>
              <table className="ag-tabelle">
                <thead>
                  <tr>
                    <th>Was</th>
                    <th className="r">Gekauft für</th>
                    <th className="r">{jahr}</th>
                    <th className="r">Noch offen</th>
                  </tr>
                </thead>
                <tbody>
                  {r.anlagen.map((a) => (
                    <tr key={a.nummer}>
                      <td>
                        {a.text} <span className="muted">{a.nummer}</span>
                      </td>
                      <td className="r num">{euro(a.nettoCents)}</td>
                      <td className="r num">{euro(a.afaCents)}</td>
                      <td className="r num">{euro(a.restCents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {r.nichtAbziehbar > 0 && <p className="muted ag-klein">Geschäftsessen: {euro(r.nichtAbziehbar)} zählen nicht (30 %).</p>}
          <p className="ag-fuss muted">Aus Ihren Rechnungen (bezahlt), Ausgaben und dem Kassenbuch. Ihr Steuerbüro prüft die Zahlen vor der Steuererklärung.</p>
        </>
      )}
    </>
  );
}

function Block({ titel, zeilen, summe }: { titel: string; zeilen: EuerZeile[]; summe: number }) {
  return (
    <div className="rt-card ag-block">
      <h2 className="ag-h2">{titel}</h2>
      {zeilen.length === 0 ? (
        <p className="muted">Noch nichts.</p>
      ) : (
        <table className="ag-tabelle">
          <tbody>
            {zeilen.map((z) => (
              <tr key={z.name}>
                <td>{z.name}</td>
                <td className="r num">{euro(z.cents)}</td>
              </tr>
            ))}
            <tr className="ag-kz-summe">
              <td>Summe</td>
              <td className="r num">{euro(summe)}</td>
            </tr>
          </tbody>
        </table>
      )}
    </div>
  );
}
